import { action } from '@reatom/framework'
import { match } from 'ts-pattern'

import { identity, pipe, Result } from '@blade/std-lib'
import { Redacted } from '@blade/std-lib'
import { User } from '@root/entities/auth'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { UnknownError } from '@root/shared'
import { ApiError, apiRequest, type UserApi } from '@root/shared/api'
import { AuthErrorCode, UserErrorCode } from '@root/shared/api/api-error-code'

import {
  OtpRequiredError,
  UserNotFoundError,
  WrongCredentialsError,
  WrongOtpError,
} from '../../auth-errors'

import type { TypeHelper } from '@blade/std-lib'
import type {
  UserRefreshCreatePayload as UserRefreshCreatePayloadDto,
  UserRegisterGoogleCreatePayload as UserRegisterGoogleCreatePayloadDto,
  UserRegisterTgCreatePayload as UserRegisterTgCreatePayloadDto,
  UserRegisterTwitterCreatePayload as UserRegisterTwitterCreatePayloadDto,
} from '@blade/trade-api-client'
import type { InviteCode } from '@root/entities/referral/model/referral-code'

export const fetchMeRequestAction = action(async (ctx) => {
  const {
    tradeApiClient,
    environment: {
      assets: { avatarsHost },
    },
  } = ctx.get(dependenciesAtom)
  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.user.userMeList, []),
  )

  return pipe(
    result,
    Result.map((it) => User.fromDto(it, avatarsHost)),
  )
}, 'fetchMeRequestAction')

interface UserRefreshCreatePayload
  extends TypeHelper.OmitKey<UserRefreshCreatePayloadDto, 'pinCode'> {
  pinCode: Redacted.Redacted
}
export const refreshTokensRequestAction = action(
  async (ctx, req: UserRefreshCreatePayload) => {
    const {
      tradeApiClient,
      environment: {
        assets: { avatarsHost },
      },
    } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userRefreshCreate, [
        {
          ...req,
          pinCode: Redacted.value(req.pinCode),
        },
      ]),
    )

    return pipe(
      result,
      Result.mapError((e) =>
        match(e)
          .when(
            ApiError.isOfCodes([
              UserErrorCode.OtpRequired,
              AuthErrorCode.OTPRequired,
              AuthErrorCode.InvalidOTP,
              AuthErrorCode.InvalidToken,
              AuthErrorCode.InvalidCredentials,
            ]),
            identity,
          )
          .otherwise(identity),
      ),
      Result.map((it) => User.fromDto(it, avatarsHost)),
    )
  },
  'refreshTokensRequestAction',
)

export const checkUsernameRequestAction = action(
  async (ctx, username: string) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userCheckUsernameList, [
        { username },
      ]),
    )

    return pipe(
      result,
      Result.map((it) => ({ ...it, username })),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'checkUsernameRequestAction',
)

export interface LoginRequest
  extends TypeHelper.OverrideKeys<
    UserApi.LoginReqDto,
    { password?: Redacted.Redacted }
  > {}

export const loginRequestAction = action(async (ctx, req: LoginRequest) => {
  const {
    tradeApiClient,
    environment: {
      assets: { avatarsHost },
    },
  } = ctx.get(dependenciesAtom)

  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.user.userLoginCreate, [
      {
        ...req,
        password: req.password ? Redacted.value(req.password) : undefined,
      },
    ]),
  )

  return pipe(
    result,
    Result.map((it) => User.fromDto(it.user, avatarsHost)),
    Result.mapError((it) => {
      if (
        ApiError.isOfCodes([
          UserErrorCode.OtpRequired,
          AuthErrorCode.OTPRequired,
        ])(it)
      ) {
        return new OtpRequiredError()
      }
      if (ApiError.isOfCode(AuthErrorCode.InvalidOTP)(it)) {
        return new WrongOtpError()
      }
      if (ApiError.isOfCode(AuthErrorCode.UserNotFound)(it)) {
        return new UserNotFoundError()
      }
      if (ApiError.isOfCode(AuthErrorCode.InvalidCredentials)(it)) {
        return new WrongCredentialsError()
      }
      return UnknownError.fallbackToUnknown(it)
    }),
  )
}, 'loginRequestAction')

interface RegisterRequest
  extends TypeHelper.OmitKey<
    UserApi.RegisterReqDto,
    'password' | 'inviteCode'
  > {
  password: Redacted.Redacted
  inviteCode: InviteCode | undefined
}
export const registerUserRequestDevAction = action(
  async (ctx, req: RegisterRequest) => {
    const {
      tradeApiClient,
      environment: {
        assets: { avatarsHost },
      },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userRegisterDevCreate, [
        {
          ...req,
          password: Redacted.value(req.password),
        },
      ]),
    )

    return pipe(
      result,
      Result.map((it) => User.fromDto(it.user, avatarsHost)),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'registerUserRequestDevAction',
)

interface UserRegisterTgCreatePayload
  extends TypeHelper.OmitKey<
    UserRegisterTgCreatePayloadDto,
    'password' | 'inviteCode'
  > {
  /**
   * The app asks for one. The web asks for nothing and sends none: the
   * server makes a password nobody has to remember, because signing in
   * there is Telegram and nothing else.
   */
  password?: Redacted.Redacted | undefined
  inviteCode: InviteCode | undefined
}
export const registerWithTelegramRequestAction = action(
  async (ctx, req: UserRegisterTgCreatePayload) => {
    const {
      tradeApiClient,
      environment: {
        assets: { avatarsHost },
      },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userRegisterTgCreate, [
        {
          ...req,
          ...(req.password ? { password: Redacted.value(req.password) } : {}),
        },
      ]),
    )

    return pipe(
      result,
      Result.map((it) => User.fromDto(it.user, avatarsHost)),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'registerWithTelegramRequestAction',
)

interface UserRegisterTwitterCreatePayload
  extends TypeHelper.OmitKey<
    UserRegisterTwitterCreatePayloadDto,
    'password' | 'inviteCode'
  > {
  password: Redacted.Redacted
  inviteCode: InviteCode | undefined
}
export const registerWithTwitterRequestAction = action(
  async (ctx, req: UserRegisterTwitterCreatePayload) => {
    const {
      tradeApiClient,
      environment: {
        assets: { avatarsHost },
      },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userRegisterTwitterCreate, [
        {
          ...req,
          password: Redacted.value(req.password),
        },
      ]),
    )

    return pipe(
      result,
      Result.map((it) => User.fromDto(it.user, avatarsHost)),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'registerWithTwitterRequestAction',
)

interface UserRegisterGoogleCreatePayload
  extends TypeHelper.OmitKey<
    UserRegisterGoogleCreatePayloadDto,
    'password' | 'inviteCode'
  > {
  password: Redacted.Redacted
  inviteCode: InviteCode | undefined
}
export const registerWithGoogleRequestAction = action(
  async (ctx, req: UserRegisterGoogleCreatePayload) => {
    const {
      tradeApiClient,
      environment: {
        assets: { avatarsHost },
      },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userRegisterGoogleCreate, [
        {
          ...req,
          password: Redacted.value(req.password),
        },
      ]),
    )

    return pipe(
      result,
      Result.map((it) => User.fromDto(it.user, avatarsHost)),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'registerWithGoogleRequestAction',
)

export const logoutRequestAction = action(async (ctx) => {
  const { tradeApiClient } = ctx.get(dependenciesAtom)

  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.user.userLogoutCreate, []),
  )

  return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
}, 'logoutRequestAction')
